import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, AlertTriangle, DollarSign, FileText, 
  Clock, CheckCircle, XCircle, Building2, Loader2, Calendar,
  BarChart3, PieChart, AlertCircle, Package, Truck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const riskColors = {
  exhausted: 'bg-red-600 text-white',
  critical: 'bg-red-100 text-red-800',
  warning: 'bg-orange-100 text-orange-800'
};

export const AnalyticsDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  
  // Dashboard data
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [revenueLeakage, setRevenueLeakage] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [poRisk, setPoRisk] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedCompany]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedCompany !== 'all' ? { company_id: selectedCompany } : {};
      
      const [companiesRes, execRes, leakageRes, billingRes, riskRes] = await Promise.all([
        api.get('/companies'),
        api.get('/dashboards/executive-summary', { params }),
        api.get('/dashboards/revenue-leakage', { params }),
        api.get('/dashboards/billing-status', { params }),
        api.get('/dashboards/client-po-risk', { params })
      ]);
      
      setCompanies(companiesRes.data);
      setExecutiveSummary(execRes.data);
      setRevenueLeakage(leakageRes.data);
      setBillingStatus(billingRes.data);
      setPoRisk(riskRes.data);
    } catch (error) {
      console.error('Error loading dashboards:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Analytics & Dashboards</h1>
          <p className="text-muted-foreground">Business intelligence and KPIs</p>
        </div>
        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
          <SelectTrigger className="w-48" data-testid="company-filter">
            <Building2 className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Companies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Executive Summary */}
      {executiveSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-700">Active Client POs</p>
                <p className="text-2xl font-bold text-blue-900">{executiveSummary.operations.active_client_pos}</p>
                <p className="text-xs text-blue-600">SAR {executiveSummary.operations.total_po_value?.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-700">Invoiced (30 days)</p>
                <p className="text-2xl font-bold text-green-900">SAR {executiveSummary.financials_30_days.invoiced?.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-emerald-700">Collected (30 days)</p>
                <p className="text-2xl font-bold text-emerald-900">SAR {executiveSummary.financials_30_days.collected?.toLocaleString()}</p>
                <p className="text-xs text-emerald-600">{executiveSummary.financials_30_days.collection_rate}% rate</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-700">Total Outstanding</p>
                <p className="text-2xl font-bold text-orange-900">SAR {executiveSummary.receivables.total_outstanding?.toLocaleString()}</p>
                <p className="text-xs text-orange-600">{executiveSummary.receivables.pending_followups} follow-ups pending</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs for detailed dashboards */}
      <Tabs defaultValue="revenue-leakage" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="revenue-leakage">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Revenue Leakage
          </TabsTrigger>
          <TabsTrigger value="billing-status">
            <BarChart3 className="h-4 w-4 mr-2" />
            Billing Status
          </TabsTrigger>
          <TabsTrigger value="po-risk">
            <AlertCircle className="h-4 w-4 mr-2" />
            Client PO Risk
          </TabsTrigger>
        </TabsList>

        {/* Revenue Leakage Tab */}
        <TabsContent value="revenue-leakage">
          {revenueLeakage && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 border-l-4 border-l-red-500">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <span className="font-semibold">Revenue at Risk</span>
                  </div>
                  <p className="text-3xl font-bold text-red-600">SAR {revenueLeakage.summary.total_revenue_at_risk?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Delivered but not invoiced</p>
                </Card>
                
                <Card className="p-4 border-l-4 border-l-orange-500">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-orange-500" />
                    <span className="font-semibold">Uncollected</span>
                  </div>
                  <p className="text-3xl font-bold text-orange-600">SAR {revenueLeakage.summary.total_uncollected?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Outstanding invoices</p>
                </Card>
                
                <Card className="p-4 border-l-4 border-l-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <span className="font-semibold">PO Capacity</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">SAR {revenueLeakage.summary.po_remaining_capacity?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Remaining PO value</p>
                </Card>
              </div>

              {/* Detailed breakdown */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Delivered not invoiced */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-red-500" />
                    Delivered but NOT Invoiced
                    <Badge className="bg-red-100 text-red-800 ml-auto">{revenueLeakage.delivered_not_invoiced.count}</Badge>
                  </h3>
                  <p className="text-2xl font-bold text-red-600 mb-3">SAR {revenueLeakage.delivered_not_invoiced.value?.toLocaleString()}</p>
                  {revenueLeakage.delivered_not_invoiced.items?.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {revenueLeakage.delivered_not_invoiced.items.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-red-50 rounded">
                          <span>{item.dn_number}</span>
                          <span>{item.client_name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No items pending</p>
                  )}
                </Card>

                {/* Timesheets not invoiced */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-500" />
                    Timesheets NOT Invoiced
                    <Badge className="bg-orange-100 text-orange-800 ml-auto">{revenueLeakage.timesheets_not_invoiced.count}</Badge>
                  </h3>
                  <p className="text-2xl font-bold text-orange-600 mb-3">SAR {revenueLeakage.timesheets_not_invoiced.value?.toLocaleString()}</p>
                  {revenueLeakage.timesheets_not_invoiced.items?.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {revenueLeakage.timesheets_not_invoiced.items.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-orange-50 rounded">
                          <span>{item.timesheet_number || item.id}</span>
                          <span>SAR {item.total_amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No timesheets pending</p>
                  )}
                </Card>

                {/* Trips not invoiced */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Truck className="h-4 w-4 text-amber-500" />
                    Transport Trips NOT Invoiced
                    <Badge className="bg-amber-100 text-amber-800 ml-auto">{revenueLeakage.trips_not_invoiced.count}</Badge>
                  </h3>
                  <p className="text-2xl font-bold text-amber-600 mb-3">SAR {revenueLeakage.trips_not_invoiced.value?.toLocaleString()}</p>
                  {revenueLeakage.trips_not_invoiced.items?.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {revenueLeakage.trips_not_invoiced.items.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-amber-50 rounded">
                          <span>{item.trip_number}</span>
                          <span>SAR {item.trip_rate?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No trips pending</p>
                  )}
                </Card>

                {/* Expired POs */}
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-700" />
                    Expired POs with Value
                    <Badge className="bg-red-200 text-red-900 ml-auto">{revenueLeakage.expired_pos_with_value.count}</Badge>
                  </h3>
                  <p className="text-2xl font-bold text-red-700 mb-3">SAR {revenueLeakage.expired_pos_with_value.value?.toLocaleString()}</p>
                  {revenueLeakage.expired_pos_with_value.items?.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {revenueLeakage.expired_pos_with_value.items.slice(0, 5).map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-red-50 rounded">
                          <span>{item.po_number}</span>
                          <span>SAR {item.remaining_value?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No expired POs</p>
                  )}
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Billing Status Tab */}
        <TabsContent value="billing-status">
          {billingStatus && (
            <div className="space-y-4">
              {/* Totals */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total Invoiced</p>
                  <p className="text-2xl font-bold">SAR {billingStatus.totals.total_invoiced?.toLocaleString()}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total Collected</p>
                  <p className="text-2xl font-bold text-green-600">SAR {billingStatus.totals.total_collected?.toLocaleString()}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total Outstanding</p>
                  <p className="text-2xl font-bold text-orange-600">SAR {billingStatus.totals.total_outstanding?.toLocaleString()}</p>
                </Card>
              </div>

              {/* Invoice Status Breakdown */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Invoice Status Breakdown</h3>
                <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
                  {Object.entries(billingStatus.invoice_statuses).map(([status, data]) => (
                    <div key={status} className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground capitalize">{status}</p>
                      <p className="text-lg font-bold">{data.count}</p>
                      <p className="text-xs">SAR {(data.value / 1000).toFixed(0)}K</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Pending Billing */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Pending Billing Items</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <Package className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                    <p className="text-2xl font-bold">{billingStatus.pending_billing.delivery_notes}</p>
                    <p className="text-sm text-muted-foreground">Delivery Notes</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <FileText className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold">{billingStatus.pending_billing.timesheets}</p>
                    <p className="text-sm text-muted-foreground">Timesheets</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <Truck className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold">{billingStatus.pending_billing.transport_trips}</p>
                    <p className="text-sm text-muted-foreground">Transport Trips</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-red-600" />
                    <p className="text-2xl font-bold">{billingStatus.pending_billing.total}</p>
                    <p className="text-sm text-muted-foreground">Total Pending</p>
                  </div>
                </div>
              </Card>

              {/* Collection Metrics */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Collection Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Average Collection Period</p>
                    <p className="text-3xl font-bold text-green-700">{billingStatus.collection_metrics.average_collection_period_days} days</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Paid Invoices (All Time)</p>
                    <p className="text-3xl font-bold text-blue-700">{billingStatus.collection_metrics.paid_invoice_count}</p>
                  </div>
                </div>
              </Card>

              {/* Monthly Trends */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Monthly Billing Trends (Last 6 Months)</h3>
                <div className="grid grid-cols-6 gap-2">
                  {billingStatus.monthly_trends.map((month, i) => (
                    <div key={i} className="text-center p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground">{month.month}</p>
                      <p className="text-lg font-bold">{month.invoice_count}</p>
                      <p className="text-xs">SAR {(month.total_value / 1000).toFixed(0)}K</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Client PO Risk Tab */}
        <TabsContent value="po-risk">
          {poRisk && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total POs</p>
                  <p className="text-2xl font-bold">{poRisk.summary.total_pos}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Active POs</p>
                  <p className="text-2xl font-bold text-green-600">{poRisk.summary.active_pos}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Risk POs</p>
                  <p className="text-2xl font-bold text-red-600">{poRisk.summary.risk_po_count}</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Total Risk Value</p>
                  <p className="text-2xl font-bold text-red-600">SAR {poRisk.summary.total_risk_value?.toLocaleString()}</p>
                </Card>
              </div>

              {/* Risk Categories */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Without Document */}
                <Card className="p-4 border-l-4 border-l-red-500">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    POs Without Document (Risk Mode)
                    <Badge className="bg-red-100 text-red-800 ml-auto">{poRisk.risk_without_document.count}</Badge>
                  </h3>
                  {poRisk.risk_without_document.items?.length > 0 ? (
                    <div className="space-y-2">
                      {poRisk.risk_without_document.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-red-50 rounded">
                          <span className="font-medium">{item.po_number}</span>
                          <span>{item.client_name}</span>
                          <span>SAR {item.total_value?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600">All POs have documents</p>
                  )}
                </Card>

                {/* Expiring Soon */}
                <Card className="p-4 border-l-4 border-l-orange-500">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-orange-500" />
                    Expiring Within 30 Days
                    <Badge className="bg-orange-100 text-orange-800 ml-auto">{poRisk.expiring_soon.count}</Badge>
                  </h3>
                  {poRisk.expiring_soon.items?.length > 0 ? (
                    <div className="space-y-2">
                      {poRisk.expiring_soon.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-orange-50 rounded">
                          <span className="font-medium">{item.po_number}</span>
                          <span>{item.days_to_expiry} days left</span>
                          <span>SAR {item.remaining_value?.toLocaleString()} remaining</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600">No POs expiring soon</p>
                  )}
                </Card>

                {/* Expired */}
                <Card className="p-4 border-l-4 border-l-red-700">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-700" />
                    Expired POs
                    <Badge className="bg-red-200 text-red-900 ml-auto">{poRisk.expired.count}</Badge>
                  </h3>
                  <p className="text-lg font-bold text-red-700 mb-2">SAR {poRisk.expired.total_remaining_value?.toLocaleString()} at risk</p>
                  {poRisk.expired.items?.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {poRisk.expired.items.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-red-50 rounded">
                          <span className="font-medium">{item.po_number}</span>
                          <span>SAR {item.remaining_value?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600">No expired POs</p>
                  )}
                </Card>

                {/* High Consumption */}
                <Card className="p-4 border-l-4 border-l-amber-500">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-amber-500" />
                    High Consumption (&gt;80%)
                    <Badge className="bg-amber-100 text-amber-800 ml-auto">{poRisk.high_consumption.count}</Badge>
                  </h3>
                  {poRisk.high_consumption.items?.length > 0 ? (
                    <div className="space-y-2">
                      {poRisk.high_consumption.items.map((item, i) => (
                        <div key={i} className="flex justify-between items-center text-sm p-2 bg-amber-50 rounded">
                          <span className="font-medium">{item.po_number}</span>
                          <Badge className={riskColors[item.status]}>{item.consumption_percentage}%</Badge>
                          <span>SAR {item.remaining_value?.toLocaleString()} left</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600">No high consumption POs</p>
                  )}
                </Card>
              </div>

              {/* Status and Type Breakdown */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <h3 className="font-semibold mb-3">PO Status Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(poRisk.status_breakdown).map(([status, data]) => (
                      <div key={status} className="flex justify-between items-center p-2 bg-muted rounded">
                        <span className="capitalize">{status}</span>
                        <span className="font-medium">{data.count} POs</span>
                        <span className="text-muted-foreground">SAR {data.value?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-3">PO Type Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(poRisk.type_breakdown).map(([type, data]) => (
                      <div key={type} className="flex justify-between items-center p-2 bg-muted rounded">
                        <span className="capitalize">{type}</span>
                        <span className="font-medium">{data.count} POs</span>
                        <span className="text-muted-foreground">SAR {data.value?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboardPage;
